import {
  Combobox,
  Group,
  Text,
  TextProps,
  useCombobox,
  Stack,
  StackProps,
  ComboboxProps,
  rem,
  GroupProps,
} from '@mantine/core';
import { PiCaretDownBold } from 'react-icons/pi';
import { Token } from 'src/components/Token/Token';

export type SelectOption = {
  value: string;
  label?: string;
};

export type SelectProps = {
  options: SelectOption[];
  label?: React.ReactNode;
  value?: string;
  valuePlaceholder?: string;
  useValueAsFallback?: boolean;
  isTokenSelect?: boolean;
  withinPortal?: boolean;
  stackProps?: StackProps;
  comboboxProps?: ComboboxProps;
  targetProps?: GroupProps;
  labelProps?: TextProps;
  onChange: (newValue: string) => void;
  onClick?: () => void;
  optionRenderer?: (
    option: SelectOption,
    labelNode: React.ReactNode
  ) => React.ReactNode;
};

export function Select({
  options,
  label,
  value: selectedValue,
  valuePlaceholder,
  useValueAsFallback = false,
  isTokenSelect = false,
  withinPortal = true,
  stackProps,
  comboboxProps,
  targetProps,
  labelProps,
  onChange,
  onClick,
  optionRenderer,
}: SelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const LabelNode = ({ children }: { children: React.ReactNode }) => {
    return (
      <Text fz={16} fw={500} style={{ flexShrink: 0 }} {...labelProps}>
        {children}
      </Text>
    );
  };

  const isClickable = options.length > 1;

  const renderOption = (value?: string, isSelectedValue?: boolean) => {
    if (typeof value === 'undefined') {
      if (isSelectedValue && valuePlaceholder) {
        return <LabelNode>{valuePlaceholder}</LabelNode>;
      }
      return null;
    }

    const option = options.find((i) => i.value === value);

    const labelNode = (
      <LabelNode>
        {option?.label || (useValueAsFallback ? value : null)}
      </LabelNode>
    );

    if (optionRenderer) {
      return optionRenderer(option!, labelNode);
    }

    return (
      <Group gap={8} wrap="nowrap" style={{ flexShrink: 0 }}>
        {isTokenSelect && <Token.Icon address={value} size={22} />}
        {labelNode}
      </Group>
    );
  };
  return (
    <Stack gap={12} align="flex-start" {...stackProps}>
      {label && (
        <Text fz={16} fw={500}>
          {label}
        </Text>
      )}

      <Combobox
        store={combobox}
        withinPortal={withinPortal}
        width="max-content"
        position="bottom-start"
        {...comboboxProps}
        onOptionSubmit={(val) => {
          onChange(val);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <Group
            gap={8}
            h={40}
            px={12}
            bg="#171B21"
            wrap="nowrap"
            justify="space-between"
            {...targetProps}
            style={{
              flexShrink: 0,
              borderRadius: rem(8),
              border: '1px solid #F1F1F114',
              cursor: isClickable ? 'pointer' : 'default',
              ...targetProps?.style,
            }}
            onClick={() =>
              onClick ? onClick() : isClickable && combobox.toggleDropdown()
            }
          >
            {renderOption(selectedValue, true)}
            {isClickable && (
              <PiCaretDownBold size={16} style={{ flexShrink: 0 }} />
            )}
          </Group>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {options.map((o, i) => {
              return (
                <Combobox.Option value={o.value} key={o.value}>
                  {renderOption(o.value)}
                </Combobox.Option>
              );
            })}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Stack>
  );
}
